export function createLiveSubFanout() {
    const entries = new Map();
    const keyFor = (symbol, timeframe) => `${symbol}::${timeframe}`;
    const subscribe = (adapter, symbol, timeframe, callback) => {
        const key = keyFor(symbol, timeframe);
        let entry = entries.get(key);
        if (!entry) {
            entry = { refCount: 0, callbacks: new Set(), upstreamUnsub: null };
            entries.set(key, entry);
            // `const` alias: the tick callback below closes over it, and TS
            // resets narrowing of a captured `let` inside a closure body.
            const created = entry;
            // Exactly 3 params per the StockDataAdapter contract (BaseAdapter.ts:77).
            created.upstreamUnsub = adapter.subscribeToBar(symbol, timeframe, (bar) => {
                // Snapshot: a pane unsubscribing mid-tick must not skip/shift the rest.
                for (const fn of [...created.callbacks]) {
                    try {
                        fn(bar);
                    }
                    catch {
                        // One pane's failure must not starve the other panes.
                    }
                }
            });
        }
        entry.refCount += 1;
        entry.callbacks.add(callback);
        let done = false;
        return () => {
            if (done)
                return;
            done = true;
            const current = entries.get(key);
            if (!current)
                return;
            current.callbacks.delete(callback);
            current.refCount -= 1;
            if (current.refCount <= 0) {
                try {
                    current.upstreamUnsub?.();
                }
                finally {
                    if (entries.get(key) === current)
                        entries.delete(key);
                }
            }
        };
    };
    return { subscribe, keyFor, size: () => entries.size };
}
/**
 * One fan-out registry per adapter instance. A WeakMap (never a shared
 * singleton keyed by symbol alone) so two adapters — e.g. two tenants in
 * one browser — can never share an upstream subscription (DATA-03).
 */
const fanoutsByAdapter = new WeakMap();
export function getLiveSubFanout(adapter) {
    let fanout = fanoutsByAdapter.get(adapter);
    if (!fanout) {
        fanout = createLiveSubFanout();
        fanoutsByAdapter.set(adapter, fanout);
    }
    return fanout;
}
